import React from "react";
import { Box, Grid, Paper, Skeleton, Stack } from "@mui/material";

/**
 * Skeleton loader for the My Listings page. Mirrors the layout
 * (back link → header + CTA → 4 stat cards → tabs row → listing rows)
 * so the loading state has the same shape as the loaded page.
 */
export default function ManageListingsSkeleton() {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Back link */}
        <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />

        {/* Header row: title/subtitle + CTA button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Skeleton variant="text" width={180} height={36} />
            <Skeleton variant="text" width={300} height={20} />
          </Box>
          <Skeleton
            variant="rounded"
            width={170}
            height={44}
            sx={{ borderRadius: "12px" }}
          />
        </Box>

        {/* Stat cards row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                  bgcolor: "background.paper",
                  height: 180,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: (theme) => theme.palette.shadow.statCard,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Skeleton
                    variant="rounded"
                    width={42}
                    height={42}
                    sx={{ borderRadius: "12px" }}
                  />
                  <Skeleton variant="circular" width={20} height={20} />
                </Stack>
                <Box>
                  <Skeleton variant="text" width="60%" height={18} />
                  <Skeleton variant="text" width="45%" height={28} />
                  <Skeleton variant="text" width="55%" height={14} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Tabs + filter row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Skeleton
            variant="rounded"
            width={420}
            height={44}
            sx={{ borderRadius: 999 }}
          />
          <Skeleton
            variant="rounded"
            width={200}
            height={40}
            sx={{ borderRadius: 999 }}
          />
        </Box>

        {/* Listing card rows */}
        <Stack spacing={2.5}>
          {[0, 1, 2].map((i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2.5}
                alignItems={{ md: "stretch" }}
              >
                {/* Image placeholder */}
                <Skeleton
                  variant="rounded"
                  width={260}
                  height={180}
                  sx={{ borderRadius: 2, flexShrink: 0 }}
                />

                {/* Right side: title, chips, info banner, metrics row */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 1 }}
                  >
                    <Skeleton variant="text" width={220} height={28} />
                    <Skeleton
                      variant="rounded"
                      width={80}
                      height={22}
                      sx={{ borderRadius: 999 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={90}
                      height={22}
                      sx={{ borderRadius: 999 }}
                    />
                  </Stack>
                  <Skeleton variant="text" width={180} height={18} sx={{ mb: 1.5 }} />

                  {/* Verification banner */}
                  <Skeleton
                    variant="rounded"
                    height={120}
                    sx={{ borderRadius: 2, mb: 2 }}
                  />

                  {/* Metrics row */}
                  <Grid container spacing={2}>
                    {[0, 1, 2, 3].map((j) => (
                      <Grid size={{ xs: 6, md: 3 }} key={j}>
                        <Skeleton variant="text" width={70} height={14} />
                        <Skeleton variant="text" width={90} height={22} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
